let getLocalDB: any;
let Navigation: any;
let Point: any;
let User: any;
let instanceToPlain: any;
let LogService: any;
// Kept OFF the module top level: a top-level `await` makes this shared package
// an async ESM module, and the Cloud Functions runtime require()s it and throws
// ERR_REQUIRE_ASYNC_MODULE on an async graph. The guard is false on the server
// (no-op); on web the imports resolve from the bundle before any DB call.
void (async () => {
  if (typeof process.env.REACT_APP_FIREBASE_API_KEY === "undefined") {
    return;
  }
  // @ts-ignore — "/src/..." absolute paths are resolved by the web bundler at
  // runtime; they are not resolvable by the node-side TypeScript program.
  const module = await import("/src/FirebaseUtils.js");
  getLocalDB = module.getLocalDB;

  // @ts-ignore — see above
  const moduleNavigation = await import("/src/entities/Navigation.js");
  Navigation = moduleNavigation.Navigation;
  // @ts-ignore — see above
  const modulePoint = await import("/src/entities/Point.js");
  Point = modulePoint.Point;
  // @ts-ignore — see above
  const moduleUser = await import("/src/entities/User.js");
  User = moduleUser.User;

  const moduleInstanceToPlain = await import("class-transformer");
  instanceToPlain = moduleInstanceToPlain.instanceToPlain;
})();

export class LocalDbService {
  static instance: LocalDbService | null = null;
  // Concurrent callers (e.g. CompareActivitiesPage loading two activities via Promise.all) must
  // await the same in-flight construction instead of each racing `!LocalDbService.instance` and
  // creating their own instance.
  static initPromise: Promise<LocalDbService> | null = null;

  // Instance of the local database (typeorm DataSource)
  db: any = null;

  // TypeORM repositories
  navigationRepo: any = null;
  pointRepo: any = null;
  userRepo: any = null;

  static getLocalDbService(): Promise<LocalDbService> {
    if (LocalDbService.instance) {
      return Promise.resolve(LocalDbService.instance);
    }
    if (!LocalDbService.initPromise) {
      LocalDbService.initPromise = (async () => {
        const instance = new LocalDbService();
        await instance.init();
        LocalDbService.instance = instance;
        return instance;
      })();
    }
    return LocalDbService.initPromise;
  }

  async init(): Promise<void> {
    const db = await getLocalDB();
    if (db) {
      this.db = db;
    } else {
      // Wait for the local database to be initialized before proceeding
      this.db = await new Promise((resolve) => {
        const interval = setInterval(() => {
          getLocalDB().then((db: any) => {
            if (db) {
              clearInterval(interval);
              resolve(db);
            }
          });
        }, 1000);
      });
    }
    this.navigationRepo = this.db.getRepository(Navigation);
    this.pointRepo = this.db.getRepository(Point);
    this.userRepo = this.db.getRepository(User);
  }

  async clear(): Promise<void> {
    // Clear all the data
    await this.navigationRepo.clear();
    await this.pointRepo.clear();
    await this.userRepo.clear();
  }

  clearExceptNavigationDataAndCurrentUser(userUidToKeep: string): Promise<any> {
    return this.userRepo.find({ID: {not: userUidToKeep}}).then((users: any[]) => {
      return Promise.all(users.map((user) => this.userRepo.remove(user)));
    });
  }

  getNavigations(): Promise<any> {
    return this.navigationRepo
      .find({
        order: {
          dS: "DESC"
        }
      })
      .then((navigations: any) => {
        return this.toJSON(navigations);
      });
  }

  getNavigationById(id: string, transaction?: any): Promise<any> {
    return (transaction ? transaction.getRepository(Navigation) : this.navigationRepo)
      .findOne({
        where: {
          ID: id
        }
      })
      .then((navigation: any) => {
        return this.toJSON(navigation);
      });
  }

  async setNavigation(navigation: any, transaction?: any): Promise<boolean> {
    try {
      const result = await (transaction ? transaction.getRepository(Navigation) : this.navigationRepo).save(navigation);
      if (!result) {
        return false;
      }
      return true;
    } catch (error) {
      return false;
    }
  }

  deleteNavigationById(id: string): Promise<any> {
    return this.navigationRepo.delete(id);
  }

  getPointsByNavId(navId: string): Promise<any> {
    return this.pointRepo
      .find({
        where: {
          nID: navId
        }
      })
      .then((points: any) => {
        return this.toJSON(points);
      });
  }

  setPoint(nID: string, m: string, point: any): Promise<any> {
    return this.pointRepo.save({...point, nID, m});
  }

  async setPointsForNavId(navId: string, points: any[], transaction?: any): Promise<boolean> {
    // Delete all the points associated with the navigation and insert the new points using bulk insert
    await this.deleteAllPoints(navId, transaction);
    // Check if the point has missing m values and assign them, also check if m values are unique, otherwise make them unique
    points = points.map((point, index) => {
      point.nID = navId;
      point.m = point.m ?? `${index}`;
      return point;
    });

    try {
      const result = await (transaction ? transaction : this.db)
        .createQueryBuilder()
        .insert()
        .into(Point)
        .values(points)
        .execute();
      // Verify all points were inserted
      let countResult = 0;
      for (let i = 0; i < result.identifiers.length; i++) {
        if (result.identifiers[i] !== undefined) countResult++;
      }
      let countPoints = 0;
      for (let i = 0; i < points.length; i++) {
        if (points[i] !== undefined) countPoints++;
      }
      if (countPoints !== countResult || countResult === 0 || countPoints === 0) {
        LogService.addErrorLogAsync("", "setPointsForNavId count points wrong", "error", false, true);
        return false;
      }
      return true;
    } catch (error) {
      LogService.addErrorLogAsync("", "setPointsForNavId count points wrong", error, "error", false, true);
      return false;
    }
  }

  deleteAllPoints(navId: string, transaction?: any): Promise<any> {
    return (transaction ? transaction.getRepository(Point) : this.pointRepo).delete({nID: navId});
  }

  deletePoints(points: any[], navID: string): Promise<any> {
    // Bulk delete points using PrimaryKey (m, nID)
    if (points.length === 0) {
      return Promise.resolve();
    }

    return this.db
      .createQueryBuilder()
      .delete()
      .from(Point)
      .where("m IN (:...m)", {m: points.map((point: any) => point.id)})
      .andWhere("nID = :nID", {nID: navID})
      .execute();
  }

  getUser(uid: string | null = null, notUid: string | null = null): Promise<any> {
    if (uid) {
      return this.userRepo
        .findOne({
          where: {
            ID: uid
          }
        })
        .then((user: any) => {
          return this.toJSON(user);
        });
    }

    if (notUid) {
      return this.userRepo.find({ID: {not: notUid}}).then((users: any[]) => {
        if (users.length > 1) {
          throw new Error("Multiple users found in the database");
        }

        if (users.length === 0) {
          return null;
        }

        return this.toJSON(users[0]);
      });
    }

    return this.userRepo.findAndCount().then(([users, count]: [any[], number]) => {
      if (count > 1) {
        throw new Error("Multiple users found in the database");
      }

      if (users.length === 0) {
        return null;
      }

      return this.toJSON(users[0]);
    });
  }

  async checkPreviousConnectedUserIsSame(userUid: string): Promise<boolean> {
    const user = await this.getUser(null, userUid);
    if (!user) {
      return true;
    }
    return user.ID === userUid;
  }

  setUser(user: any): Promise<any> {
    return this.userRepo.save(user);
  }

  getEquipementById(id: string): Promise<any> {
    // TODO: Implement this method
    return Promise.resolve(null);
  }

  setEquipement(key: string, equipement: any): void {
    // TODO: Implement this method
  }

  toJSON(entity: any): any {
    // It should map an typeORM entity to a JSON object and delete many-to-one/one-to-many relationships and also custom_id field
    return instanceToPlain(entity);
  }
}
